import type {
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemParams,
  CustomSeriesRenderItemReturn,
  EChartsOption,
  ECElementEvent,
  TooltipComponentFormatterCallbackParams,
} from 'echarts'
import type {
  FlameFrame,
  ProfileNode,
} from '@app/shared/flame-graph/flame-frames'
import {
  buildFlameFrames,
  maxFrameLevel,
  ROOT_FRAME_ID,
} from '@app/shared/flame-graph/flame-frames'
import { EchartsHost } from '@app/shared/echarts/echarts-host'

const ROW_HEIGHT = 22
// Frames narrower than this (px) are not drawn, so the canvas isn't cluttered
// with slivers.
const MIN_FRAME_WIDTH = 4

// Renders a `{ name, value (seconds), children }` profile tree as an ECharts
// flame graph inside `container`, which must be attached to the DOM.
export default async function renderFlameGraph(
  container: HTMLElement,
  profileData: ProfileNode
): Promise<void> {
  const frames = buildFlameFrames(profileData)
  const maxLevel = maxFrameLevel(frames)

  // A block-level div already fills the debug pane width, which ECharts reads
  // on init; only the height needs to be explicit since the container has no
  // intrinsic height.
  container.style.width = '100%'
  container.style.height = `${(maxLevel + 1) * ROW_HEIGHT}px`

  // Nothing disposes this host; the guard only satisfies mount()'s nullable
  // return.
  const chart = await new EchartsHost().mount(container)
  if (!chart) return

  const renderItem = (
    _params: CustomSeriesRenderItemParams,
    api: CustomSeriesRenderItemAPI
  ): CustomSeriesRenderItemReturn => {
    const level = Number(api.value(0))
    // Each frame occupies the full y band [level, level + 1] rather than
    // centring on the level line, so the top (root) row isn't clipped by the
    // plot edge.
    const topLeft = api.coord([api.value(1), level])
    const bottomRight = api.coord([api.value(2), level + 1])
    const rectWidth = bottomRight[0] - topLeft[0]
    const rectHeight = bottomRight[1] - topLeft[1]

    // Skip slivers too narrow to read, as the old flame graph did.
    if (rectWidth < MIN_FRAME_WIDTH) return

    return {
      type: 'rect',
      transition: ['shape'],
      shape: {
        x: topLeft[0],
        y: topLeft[1],
        width: rectWidth,
        height: rectHeight - 2,
        r: 2,
      },
      style: { fill: api.visual('color') },
      emphasis: { style: { stroke: '#000' } },
      textConfig: { position: 'insideLeft' },
      textContent: {
        type: 'text',
        style: {
          text: String(api.value(3)),
          fill: '#000',
          width: rectWidth - 4,
          overflow: 'truncate',
          ellipsis: '..',
          truncateMinChar: 1,
        },
      },
    }
  }

  let focusId = ROOT_FRAME_ID

  const optionFor = (data: FlameFrame[]): EChartsOption => ({
    tooltip: {
      formatter: (params: TooltipComponentFormatterCallbackParams): string => {
        // The tooltip fires only over this chart's custom series, whose data
        // rows are FlameFrames, so the union/array arms cannot occur here.
        const value = (Array.isArray(params) ? params[0] : params)
          .value as FlameFrame['value']
        const ms = ((value[2] - value[1]) * 1000).toFixed(2)
        return `${value[3]} (${ms} ms)`
      },
    },
    // Zero grid so the frames fill the container edge-to-edge; the default
    // grid reserves ~10% side margins and a 60px top band, which offsets the
    // graph and clips the top (root) frame.
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    xAxis: { type: 'value', show: false, max: data[0]?.value[2] ?? 0 },
    // Inverse axis draws the root at the top with children fanning downward.
    // The axis spans one extra unit so every level (0..maxLevel) gets a full band.
    yAxis: {
      type: 'value',
      show: false,
      inverse: true,
      min: 0,
      max: maxLevel + 1,
    },
    series: [
      {
        type: 'custom',
        renderItem,
        encode: { x: [1, 2], y: 0 },
        data,
      },
    ],
  })

  chart.setOption(optionFor(frames))

  // Click a frame to zoom into its subtree; click the focused (top) frame again
  // to reset to the full tree.
  chart.on('click', (params: ECElementEvent) => {
    const clickedId = (params.data as FlameFrame | undefined)?.id
    if (clickedId === undefined) return
    focusId = clickedId === focusId ? ROOT_FRAME_ID : clickedId
    chart.setOption(optionFor(buildFlameFrames(profileData, focusId)))
  })
}
